import {
  ISingleOperationPayload,
  IOperationHandlerThunkAction,
  createSingleItemOperationHandler,
  registerOperationHandler
} from '@ms/items-view/lib/private/addons/registerOperationHandler';
import { spItemKeyFacet, spFolderItemSetKeyFacet } from '../../../dataSource/Keys';
import { getItemsOperation } from '@ms/items-view/lib/private/operations/getItems';
import { IItemCacheDispatch } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { ItemResolver } from '@ms/items-view/lib/private/models/items/ItemResolver';
import { getContentTypes, constructGetItemContext } from '../../../dataSource/getServerDataContext';
import { getServerData } from '../../../dataSource/getServerData';
import { processResponse } from '../../../dataSource/processResponse';
import { defaultChildItemSetFacet } from '@ms/items-view/lib/private/facets/SetFacets';
import { uniqueIdFacet, onProcessListItemResponseConfigurationFacet } from '../../../dataSource/Facets';
import { IOperationHandler } from '@ms/items-view/lib/private/store/actions/implementOperation';
import { getAllAncestorItems, getParentItemKey } from '../../../dataSource/operations/getAllAncestorItems';
import { readConfiguration } from '@ms/items-view/lib/private/operations/configuration/readConfiguration';
import { normalizeLower } from '@ms/items-view-utilities/lib/guid/Guid';
import { itemsViewPerfMarkers, PerfMark } from '../../../utilities/logging/performance/PerfMarkers';
import {
  createAddon,
  IAddon
} from '@ms/items-view/lib/private/addons/registerOperationHandler';

const spItemGetItemsOperationHandler:
  IOperationHandler<typeof spItemKeyFacet.type, typeof getItemsOperation.type> =
  createSingleItemOperationHandler({
    maxParallelOperationCount: 8
  })(getSPItem);

function getSPItem(
  payload: ISingleOperationPayload<typeof spItemKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  PerfMark.mark(itemsViewPerfMarkers.singleItemFetch.start);

  const {
    itemKey
  } = payload;

  const key = spItemKeyFacet.deserialize(itemKey);

  return async (dispatch: IItemCacheDispatch) => {
    let itemResolver = new ItemResolver();

    const itemSetKey = {
      webAbsoluteUrl: key.webAbsoluteUrl,
      listFullUrl: key.listFullUrl,
      rootFolder: key.rootFolder || ''
    };

    const contentTypes = dispatch(getContentTypes({ itemSetKey }));
    const getItemContext = dispatch(constructGetItemContext(key, contentTypes, 'getSPItem'));
    const serverData = await dispatch(getServerData(getItemContext));
    const isItemSetRequest = false;

    PerfMark.mark(itemsViewPerfMarkers.singleItemFetch.end);

    const {
      itemResolver: itemSetResolver,
      childKeys
    } = dispatch(processResponse({
      serverData,
      itemSetKey,
      currentPageContext: getItemContext.currentPageContext,
      contentTypes,
      isItemSetRequest
    }));

    const [firstItemKey] = childKeys;
    const serializedItemSetKey: string = spFolderItemSetKeyFacet.serialize(itemSetKey);

    // Need to mapKeys before merge the itemSetResolver to avoid map key in the wrong order.
    if (firstItemKey) {
      itemResolver.mapKeys({
        mappings: {
          [firstItemKey]: itemKey
        }
      });
    }

    itemResolver.merge(itemSetResolver);

    if (spItemKeyFacet.matches(firstItemKey)) {
      const spItem = spItemKeyFacet.deserialize(firstItemKey);

      const parentItem = getParentItemKey(spItem);

      if (parentItem) {
        itemResolver.merge(dispatch(getAllAncestorItems({
          parentKey: parentItem,
          serverData
        })).itemResolver);
      }
    }

    itemResolver.resolveItems({
      items: {
        [spItemKeyFacet.serialize(itemSetKey)]: {
          ...defaultChildItemSetFacet.pack({
            itemKey: serializedItemSetKey
          })
        }
      }
    });

    const {
      onProcessListItemResponseConfiguration
    } = dispatch(readConfiguration({
      onProcessListItemResponseConfiguration: onProcessListItemResponseConfigurationFacet
    }));
    if (onProcessListItemResponseConfiguration) {
      dispatch(onProcessListItemResponseConfiguration({
        itemSetKey: serializedItemSetKey,
        serverData: serverData,
        isItemSetRequest
      }));
    }

    dispatch(itemResolver.execute());

    if (payload.options.demands[uniqueIdFacet.id] && !key.rootFolder && !key.itemId) {
      // This was the request for the root folder of a list, and uniqueId is required.

      const {
        getListRootFolder
      } = await import(/* webpackChunkName: 'spaddondataonlydeferred' */'../../../dataSource/getListRootFolder');

      const listRootFolderReponse = await dispatch(getListRootFolder({
        webAbsoluteUrl: key.webAbsoluteUrl,
        listFullUrl: key.listFullUrl,
        qosName: 'getSPItem'
      }));

      itemResolver = new ItemResolver();

      const uniqueId = listRootFolderReponse.d && listRootFolderReponse.d.UniqueId;

      itemResolver.resolveItems({
        items: {
          [itemKey]: {
            ...(uniqueId ? uniqueIdFacet.pack(normalizeLower(uniqueId)) : {})
          }
        }
      });

      dispatch(itemResolver.execute());
    }
  };
}

export const getSPItemsAddon: IAddon = createAddon(() => {
  return async (dispatch: IItemCacheDispatch) => {
    dispatch(registerOperationHandler(spItemKeyFacet, getItemsOperation)(
      spItemGetItemsOperationHandler
    ));
  };
});